import React from 'react';
import { 
  Users, 
  Send, 
  Eye, 
  Award, 
  Activity, 
  Sparkles, 
  Upload, 
  Plus, 
  BarChart3, 
  Zap,
  Mail,
  MessageSquare
} from 'lucide-react';
import StatCard from '../components/StatCard';

const DashboardPage = ({ 
  contactCounts, 
  campaigns, 
  templates, 
  deliverabilityScore, 
  setCurrentPage,
  automationMode = 'off'
}) => {
  const totalSent = campaigns.reduce((acc, c) => acc + c.sent, 0);
  const totalOpened = campaigns.reduce((acc, c) => acc + c.opened, 0);
  const avgOpenRate = totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : 0;
  const activeContacts = contactCounts?.active || 0;
  const suppressedContacts = contactCounts?.suppressed || 0;
  const sentCampaigns = campaigns.filter(c => c.status === 'sent').length;
  const scheduledCampaigns = campaigns.filter(c => c.status === 'scheduled').length;
  const openRateTrend = totalSent > 0 ? (avgOpenRate > 30 ? 'up' : 'down') : 'down';
  const hasDeliverabilityScore = typeof deliverabilityScore === 'number';
  const deliverabilityTrend = hasDeliverabilityScore ? (deliverabilityScore > 80 ? 'up' : 'down') : 'down';
  const deliverabilityValue = hasDeliverabilityScore ? `${deliverabilityScore}%` : '—';
  const deliverabilityStatus = hasDeliverabilityScore
    ? (deliverabilityScore > 80 ? 'Strong sender rep' : 'Needs attention')
    : 'Not enough data';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's your marketing overview.</p>
        </div>
        <button 
          onClick={() => setCurrentPage('campaigns')}
          className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
        >
          <Zap size={20} />
          Create Campaign
        </button>
      </div>

      <button
        type="button"
        onClick={() => setCurrentPage('settings')}
        className={`w-full rounded-2xl border px-5 py-4 text-left ${
          automationMode === 'on'
            ? 'border-red-200 bg-red-50'
            : automationMode === 'test'
              ? 'border-amber-200 bg-amber-50'
              : 'border-gray-200 bg-white'
        }`}
      >
        <div className="text-sm font-semibold text-gray-900">
          TRADEIT DAY email is {automationMode === 'on' ? 'on' : automationMode === 'test' ? 'in test' : 'off'}
        </div>
        <div className="mt-1 text-sm text-gray-600">
          {automationMode === 'test'
            ? 'Only johncarterson101@gmail.com receives these emails. Open settings to turn the real list on.'
            : automationMode === 'on'
              ? 'The sequence can send. Open settings to turn it off.'
              : 'Registration still updates people. Open settings when you are ready to send.'}
        </div>
      </button>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          label="Active Contacts" 
          value={activeContacts} 
          icon={Users} 
          color="from-blue-500 to-cyan-500" 
          change={`Suppressed: ${suppressedContacts}`}
          trend={activeContacts >= suppressedContacts ? 'up' : 'down'} 
        />
        <StatCard 
          label="Campaigns Sent" 
          value={sentCampaigns} 
          icon={Send} 
          color="from-purple-500 to-pink-500" 
          change={`Scheduled: ${scheduledCampaigns}`}
          trend={sentCampaigns > 0 ? 'up' : 'down'} 
        />
        <StatCard 
          label="Avg Open Rate" 
          value={`${avgOpenRate}%`} 
          icon={Eye} 
          color="from-green-500 to-emerald-500" 
          change={totalSent > 0 ? `Based on ${totalSent} messages` : 'No sends yet'}
          trend={openRateTrend} 
        />
        <StatCard 
          label="Deliverability" 
          value={deliverabilityValue} 
          icon={Award} 
          color="from-orange-500 to-red-500" 
          change={deliverabilityStatus}
          trend={deliverabilityTrend} 
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
            <Activity className="text-indigo-600" size={20} />
          </div>
          <div className="space-y-3">
            {campaigns.slice(0, 5).map(campaign => {
              const openRate = campaign.channel === 'email' && campaign.delivered > 0 
                ? ((campaign.opened / campaign.delivered) * 100).toFixed(1) 
                : 0;
              
              return (
                <div 
                  key={campaign.id} 
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl hover:shadow-md transition-all duration-300 cursor-pointer" 
                  onClick={() => setCurrentPage('reports')}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${campaign.channel === 'email' ? 'bg-indigo-100' : 'bg-purple-100'} rounded-lg`}>
                      {campaign.channel === 'email' 
                        ? <Mail className="text-indigo-600" size={18} /> 
                        : <MessageSquare className="text-purple-600" size={18} />
                      }
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-600">
                        {campaign.recipientCount} recipients • {new Date(campaign.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-green-600">
                      {campaign.channel === 'email' ? `${openRate}% open` : `${campaign.replied} replies`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {((campaign.delivered / campaign.sent) * 100).toFixed(1)}% delivered
                    </div>
                  </div>
                </div>
              );
            })}
            {campaigns.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Send className="mx-auto mb-3 text-gray-400" size={48} />
                <p className="text-lg font-semibold mb-1">No campaigns yet</p>
                <p className="text-sm">Create your first campaign to get started!</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
            <Sparkles className="text-purple-600" size={20} />
          </div>
          <div className="space-y-3">
            {[
              { label: 'Upload Contacts', icon: Upload, page: 'upload', color: 'from-blue-500 to-cyan-500', desc: 'Import from CSV/mbox' },
              { label: 'New Template', icon: Plus, page: 'templates', color: 'from-purple-500 to-pink-500', desc: 'Create template' },
              { label: 'Send Campaign', icon: Send, page: 'campaigns', color: 'from-green-500 to-emerald-500', desc: 'Launch campaign' },
              { label: 'View Analytics', icon: BarChart3, page: 'reports', color: 'from-orange-500 to-red-500', desc: 'Check performance' }
            ].map((action, i) => {
              const Icon = action.icon;
              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(action.page)}
                  className={`w-full p-4 bg-gradient-to-r ${action.color} text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-3`}
                >
                  <Icon size={20} />
                  <div className="text-left flex-1">
                    <div className="font-semibold text-sm">{action.label}</div>
                    <div className="text-xs opacity-90">{action.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
